<template>
  <DSSuggestionCombobox
    :id
    :model-value
    :options
    :suggestion="comboboxSuggestion"
    :placeholder
    :empty-search-label
    :empty-label
    :loading
    loading-label="Looking for the right account…"
    :askable
    :disabled
    :class="props.class"
    @update:model-value="emit('update:modelValue', $event)"
    @select="handleSelect"
    @ask="emit('ask')"
  >
    <template
      v-if="$slots.note"
      #note
    >
      <slot name="note" />
    </template>
  </DSSuggestionCombobox>
</template>

<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'

import { DSSuggestionCombobox, type SuggestionComboboxOption } from '@ds'

import { AccountOption, AccountSuggestion } from '../utils/types'

/*
 * Pick the account something hangs off, from a list the caller has already
 * scoped — with one suggestion pinned above it, searchable without losing it.
 *
 * The arrangement itself is DSSuggestionCombobox; this is the domain half of it:
 * an account's name is the row, its type is the aside, and its ticker is
 * searchable because that is how the rest of the app identifies an asset.
 *
 * Presentational, like TagPicker: it holds no accounts, resolves nothing, and
 * decides nothing about which are eligible. Where the candidates come from is a
 * question about the transaction being posted, and where the suggestion comes
 * from is a question about the assistant; both belong to the screen.
 */
const props = defineProps<{
  /** The chosen account's id. */
  modelValue?: string
  /** The candidates, already narrowed to whatever the caller allows. */
  accounts: AccountOption[]
  /**
   * Pinned above the list and exempt from the search. Ignored when its account
   * is not among the candidates.
   */
  suggestion?: AccountSuggestion
  placeholder?: string
  emptySearchLabel?: string
  /** Shown when the caller had no accounts to offer at all. */
  emptyLabel?: string
  /** The assistant is still deciding which account to suggest. */
  loading?: boolean
  /** Offers the "Ask AI" row — see DSSuggestionCombobox. */
  askable?: boolean
  /** `id` of the input, so a caller's label can point at it. */
  id?: string
  disabled?: boolean
  class?: HTMLAttributes['class']
}>()

const {
  emptySearchLabel = 'No account matches that.',
  emptyLabel = 'No account to pick.',
} = props

const emit = defineEmits<{
  (e: 'update:modelValue', id: string): void
  /** The account itself — a caller usually needs its name and type as well. */
  (e: 'select', account: AccountOption): void
  /** The "Ask AI" row was clicked. */
  (e: 'ask'): void
}>()

const options = computed<SuggestionComboboxOption[]>(() =>
  props.accounts.map(account => ({
    value: account.id,
    label: account.name,
    hint: account.type,
    // The ticker is searchable but never drawn: it is usually empty for the
    // kinds of asset that act as accounts, and it is how they are identified.
    searchText: `${account.name} ${account.ticker} ${account.type}`,
  })),
)

const comboboxSuggestion = computed(() =>
  props.suggestion
    ? { value: props.suggestion.accountId, reason: props.suggestion.reason }
    : undefined,
)

const handleSelect = (option: SuggestionComboboxOption) => {
  const account = props.accounts.find(item => item.id === option.value)
  if (account) emit('select', account)
}
</script>
